<template>
  <div id="app">
    <!-- Estado de carregamento durante verificação de sessão -->
    <LoadingScreen v-if="!appReady" :done="isInitialized" @finished="appReady = true" />

    <template v-else>
      <!-- Sidebar (only when authenticated) -->
      <Navbar
        v-if="showSidebar"
        :collapsed="sidebarCollapsed"
        @toggle-collapse="sidebarCollapsed = !sidebarCollapsed"
      />

      <!-- Barra do topo (avatar do usuário + novidades) — só a partir de lg.
           No mobile ela fica dentro do Navbar (TopBar placement="sidebar"). -->
      <TopBar v-if="showSidebar && isDesktop" />

      <!-- Main content -->
      <main
        class="transition-all duration-300 ease-in-out min-h-screen"
        style="background-color: hsl(var(--background))"
        :class="showSidebar ? [
          sidebarCollapsed ? 'lg:ml-[76px]' : 'lg:ml-[252px]'
        ] : ''"
      >
        <div class="route-transition-root">
          <router-view v-slot="{ Component, route }">
            <Transition name="tab-fade">
              <div v-if="Component" :key="route.path" class="route-transition-page">
                <Suspense>
                  <component :is="Component" />
                  <template #fallback>
                    <div class="route-transition-fallback" aria-hidden="true" />
                  </template>
                </Suspense>
              </div>
            </Transition>
          </router-view>
        </div>
      </main>

      <!-- Loading overlay -->
      <LoadingSpinner v-if="isLoading" />

      <!-- Toast notifications -->
      <div class="toast-container" :class="{ 'toast-container--with-topbar': showSidebar && isDesktop }">
        <Toast
          v-for="toast in toasts"
          :key="toast.id"
          :type="toast.type"
          :title="toast.title"
          :message="toast.message"
          :duration="toast.duration"
          :auto-close="toast.autoClose"
          @close="removeToast(toast.id)"
        />
      </div>

      <div
        v-if="showPrivacyBanner"
        class="fixed bottom-4 left-1/2 z-[1000] w-[calc(100%-1.5rem)] max-w-2xl -translate-x-1/2 rounded-2xl border border-zinc-200 bg-white/95 px-4 py-3 shadow-2xl backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95"
      >
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p class="text-sm font-semibold text-zinc-900 dark:text-white">Privacidade e consentimento</p>
            <p class="text-sm text-zinc-600 dark:text-zinc-400">
              Usamos cookies e dados de sessão para manter o acesso seguro. Ao continuar, você concorda com a
              <router-link :to="{ name: 'PrivacyPolicy' }" class="font-medium text-primary-600 underline">Política de Privacidade</router-link>.
            </p>
          </div>
          <button
            class="rounded-full bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700"
            @click="acceptPrivacy"
          >
            Entendi
          </button>
        </div>
      </div>

      <!-- Forçar troca de senha (primeiro acesso) -->
      <Modal
        :open="mustChangePassword"
        title="Altere sua senha"
        description="Por segurança, defina uma senha personalizada."
        size="lg"
        :closable="false"
        :close-on-overlay-click="false"
      >
        <form id="force-password-form" @submit.prevent="submitPasswordChange">
          <div class="rounded-xl border border-slate-200 dark:border-zinc-700 p-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <!-- Inputs, um abaixo do outro -->
              <div class="space-y-3">
                <div class="form-group">
                  <label class="form-label">Nova senha</label>
                  <input
                    v-model="pwForm.newPassword"
                    type="password"
                    placeholder="Nova senha"
                    class="form-input"
                    required
                    minlength="8"
                    maxlength="16"
                  />
                </div>
                <div class="form-group">
                  <label class="form-label">Confirmar nova senha</label>
                  <input
                    v-model="pwForm.confirmPassword"
                    type="password"
                    placeholder="Confirmar nova senha"
                    class="form-input"
                    required
                  />
                </div>
              </div>
              <!-- Requisitos da senha, em coluna ao lado -->
              <div class="sm:pl-5 sm:border-l sm:border-slate-100 dark:sm:border-zinc-800">
                <p class="text-xs font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wider mb-2">
                  Requisitos da senha
                </p>
                <PasswordRequirements :password="pwForm.newPassword" />
              </div>
            </div>
          </div>
          <p v-if="pwError" class="text-xs text-primary-500 mt-3">{{ pwError }}</p>
        </form>

        <template #footer>
          <button
            type="submit"
            form="force-password-form"
            class="w-full btn-primary py-2.5 text-sm justify-center"
            :disabled="pwSaving"
          >
            <span v-if="pwSaving" class="loading-spinner w-4 h-4 mr-1" />
            Salvar nova senha
          </button>
        </template>
      </Modal>
      <!-- Aceite de Termos e Política de Privacidade -->
      <Modal
        :open="showLegalAcceptanceModal"
        title="Documentos legais"
        description="Para continuar, leia e aceite os documentos abaixo."
        size="lg"
        variant="gray"
        :closable="false"
        :close-on-overlay-click="false"
      >
        <div class="rounded-xl border border-slate-200 dark:border-zinc-700 p-4 space-y-4">
          <div class="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              class="btn-secondary text-xs px-3 py-2 justify-center"
              @click="openLegalDocModal('terms')"
            >
              Ler Termos de Uso
            </button>
            <button
              type="button"
              class="btn-secondary text-xs px-3 py-2 justify-center"
              @click="openLegalDocModal('privacy')"
            >
              Ler Política de Privacidade
            </button>
          </div>
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              v-model="legalAccepted"
              type="checkbox"
              class="w-4 h-4 rounded accent-primary-500"
            />
            <span class="text-sm text-slate-700 dark:text-zinc-300">
              Li e aceito {{ pendingLegalLabel }}.
            </span>
          </label>
          <p v-if="legalError" class="text-xs text-danger-500">{{ legalError }}</p>
        </div>
        <template #footer>
          <button
            type="button"
            class="w-full btn-primary py-2.5 text-sm justify-center"
            :disabled="!legalAccepted || legalSaving"
            @click="submitLegalAcceptance"
          >
            <span v-if="legalSaving" class="loading-spinner w-4 h-4 mr-1" />
            Continuar
          </button>
        </template>
      </Modal>
      <LegalDocumentModal
        :open="legalDocModal.open"
        :document="legalDocModal.document"
        @close="legalDocModal.open = false"
      />
      <!-- Aceite do Contrato de Prestação de Serviço — só depois de Termos/
           Privacidade resolvidos, pra não empilhar dois modais bloqueantes. -->
      <ServiceContractModal
        :open="showServiceContractModal"
        blocking
        @accepted="authStore.clearPendingServiceContract()"
      />
    </template>
    <!-- PWA: registro do service worker + avisos de versão nova e sem conexão -->
    <PwaStatus />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useAuthStore } from './stores/auth'
import { useToast } from './composables/useToast'
import { useDarkMode } from './composables/useDarkMode.js'
import { useIdleLogout } from './composables/useIdleLogout.js'
import { useRoute, useRouter } from 'vue-router'
import api from './api/client'
import Navbar from './components/layout/Navbar.vue'
import TopBar from './components/layout/TopBar.vue'
import LoadingScreen from './components/common/LoadingScreen.vue'
import LoadingSpinner from './components/common/LoadingSpinner.vue'
import Toast from './components/ui/Toast.vue'
import PwaStatus from './components/common/PwaStatus.vue'
import Modal from './components/ui/Modal.vue'
import PasswordRequirements from './components/common/PasswordRequirements.vue'
import LegalDocumentModal from './components/legal/LegalDocumentModal.vue'
import ServiceContractModal from './components/legal/ServiceContractModal.vue'
import { isPasswordValid, PASSWORD_REQUIREMENTS_MESSAGE } from './utils/passwordPolicy'
import { useMediaQuery } from './composables/useMediaQuery.js'

const authStore = useAuthStore()
// lg = 1024px: a partir daqui a TopBar fica fixa no canto; abaixo, dentro do Navbar.
const isDesktop = useMediaQuery('(min-width: 1024px)')
const route = useRoute()
const router = useRouter()
const { toasts, removeToast } = useToast()
const { initializeDarkMode } = useDarkMode()

useIdleLogout()

const isAuthenticated = computed(() => authStore.isAuthenticated)
const isLoading = computed(() => authStore.isLoading)
const isInitialized = computed(() => authStore.isInitialized)
// Controla a troca de fato pra tela real — só vira true depois da
// LoadingScreen terminar a animação de sucesso (anel + check), não no
// instante exato em que isInitialized vira true. Ver LoadingScreen.vue.
const appReady = ref(false)
const mustChangePassword = computed(() => authStore.mustChangePassword)
const showSidebar = computed(() =>
  authStore.isAuthenticated &&
  authStore.user?.role !== 'CLIENT' &&
  route.name !== 'PublicBooking'
)

const sidebarCollapsed = ref(false)
const showPrivacyBanner = ref(!localStorage.getItem('avance-consent'))

const pwForm = ref({ newPassword: '', confirmPassword: '' })
const pwError = ref('')
const pwSaving = ref(false)

const hasPendingLegal = computed(() => authStore.hasPendingLegal)
const isLegalDocumentRoute = computed(() =>
  route.name === 'TermsOfUse' || route.name === 'PrivacyPolicy'
)
const showLegalAcceptanceModal = computed(() =>
  isAuthenticated.value &&
  hasPendingLegal.value &&
  !mustChangePassword.value &&
  authStore.user?.role !== 'PLATFORM_OWNER' &&
  !isLegalDocumentRoute.value
)
const legalAccepted = ref(false)
const legalError = ref('')
const legalSaving = ref(false)

// Abre o texto num modal em vez de navegar (ver LegalDocumentModal.vue) —
// sair pra /termos ou /privacidade fecharia o fluxo de aceite bloqueante.
const legalDocModal = reactive<{ open: boolean; document: 'terms' | 'privacy' | null }>({ open: false, document: null })
const openLegalDocModal = (doc: 'terms' | 'privacy') => {
  legalDocModal.document = doc
  legalDocModal.open = true
}

const LEGAL_DOCUMENT_LABELS: Record<string, string> = {
  TERMS_OF_USE: 'os Termos de Uso',
  PRIVACY_POLICY: 'a Política de Privacidade',
}
const pendingLegalLabel = computed(() =>
  authStore.pendingLegal
    .map((d: { document_type: string; version: string }) => LEGAL_DOCUMENT_LABELS[d.document_type] || d.document_type)
    .join(' e ')
)

// Aceite do Contrato de Prestação de Serviço — só o ADMIN é bloqueado (é o
// único papel com permissão de aceitar, ver requireAdmin em
// serviceContract.js no backend); pendingServiceContract já vem false pra
// qualquer outro papel. Espera Termos/Privacidade serem resolvidos primeiro
// pra não empilhar dois modais bloqueantes ao mesmo tempo.
const showServiceContractModal = computed(() =>
  isAuthenticated.value &&
  !mustChangePassword.value &&
  !hasPendingLegal.value &&
  authStore.pendingServiceContract
)

const submitLegalAcceptance = async () => {
  legalError.value = ''
  if (!legalAccepted.value) return
  legalSaving.value = true
  try {
    const docs = authStore.pendingLegal
    for (const doc of docs) {
      await api.post('/users/me/accept-terms', {
        document_type: doc.document_type,
        version: doc.version,
      })
    }
    authStore.clearPendingLegal()
    legalAccepted.value = false
  } catch (err: any) {
    legalError.value = err.response?.data?.error || 'Erro ao registrar aceite. Tente novamente.'
  } finally {
    legalSaving.value = false
  }
}


const acceptPrivacy = () => {
  localStorage.setItem('avance-consent', 'true')
  showPrivacyBanner.value = false
}

const submitPasswordChange = async () => {
  pwError.value = ''
  if (!isPasswordValid(pwForm.value.newPassword)) {
    pwError.value = PASSWORD_REQUIREMENTS_MESSAGE
    return
  }
  if (pwForm.value.newPassword !== pwForm.value.confirmPassword) {
    pwError.value = 'As senhas não coincidem.'
    return
  }
  pwSaving.value = true
  try {
    await api.put('/users/me/password', { new_password: pwForm.value.newPassword })
    authStore.clearMustChangePassword()
    pwForm.value = { newPassword: '', confirmPassword: '' }
  } catch (err: any) {
    pwError.value = err.response?.data?.error || 'Erro ao alterar senha.'
  } finally {
    pwSaving.value = false
  }
}

onMounted(async () => {
  initializeDarkMode()
  await authStore.checkAuth()
})
</script>

<style scoped>
#app {
  min-height: 100vh;
}


.toast-container {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 400px;
  width: 100%;
}

/* Com a TopBar (avatar/novidades) fixa no canto superior direito, os toasts
   começam abaixo dela em vez de sobrepor. */
.toast-container--with-topbar {
  top: 4.5rem;
}

@media (max-width: 640px) {
  .toast-container {
    left: 1rem;
    right: 1rem;
    max-width: none;
  }
}
</style>
